import { useCreateEvent } from "@/features/events/hooks/useCreateEvent";
import { EventType } from "@/features/events/types/EventType";
import { useNutritionistConsult } from "@/features/experts/hooks/useNutritionistConsult";
import { useSpecialistConsult } from "@/features/experts/hooks/useSpecialistConsult";
import { useHealthAssessment } from "@/features/health-assessment/hooks/useHealthAssessment";
import { useDnaTest } from "@/features/holifya-tests/dna-test/hooks/useDnaTest";
import { useMembership } from "@/features/membership/hooks/useMembership";
import { SubType } from "@/features/membership/types/SubType";
import { useGetDocuments } from "@/features/ocr/hooks/useGetDocuments";
import { NavigateFunction } from "react-router-dom";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import { saveEvent } from "../../../../utils/apis/user-service";
import { handleHourAndMinutes } from "../../../../utils/Functions";
import { useUserStore } from "../../../../utils/stores/useUserStore";
import { Appointment } from "../../../appointments/types/Appointment";
import { ExpertRole } from "../../../experts/types/ExpertRole";
import { useMetabolicUserType } from "../../../metabolic-health/hooks/useMetabolicHealth";
import { MetabolicUserType } from "../../../metabolic-health/types/MetabolicUserType";
import { useDnaTestWidget } from "../../hooks/useDnaTestWidget";
import { useSubHealthDnaWidget } from "../../hooks/useSubHealthDnaWidget";
import { useSubHealthWidget } from "../../hooks/useSubHealthWidget";
import { useSubNutritionWidget } from "../../hooks/useSubNutritionWidget";
import { getDisplayDate } from "../../utils/functions";
import { WidgetCard } from "./WidgetCard";

const ControlPanelWidgetSingleElement: React.FC<{ navigate: NavigateFunction }> = ({ navigate }) => {
    const metabolicUserType = useMetabolicUserType();
    const membership = useMembership()
    const { dnaTestUser } = useDnaTest()

    return dnaTestUser && membership === SubType.NONE ? <DnaTestWidget /> :
        membership && membership === SubType.HEALTH ? <SubHealthWidget /> :
            membership && membership === SubType.HEALTH_DNA ? <SubHealtDnaWidget /> :
                membership && membership === SubType.NUTRITION ? <SubNutritionWidget /> :
                    metabolicUserType === MetabolicUserType.Hook ? <MetabolicHookWidget navigate={navigate} /> :
                        <MetabolicSubWidget navigate={navigate} />;
};

export default ControlPanelWidgetSingleElement

const DnaTestWidget = () => {
    const { text, buttonText, onClick, variant } = useDnaTestWidget()
    return (
        <WidgetCard
            text={text}
            buttonText={buttonText}
            onClick={onClick}
            variant={variant}
        />
    )
}

const SubHealthWidget = () => {
    const { text, buttonText, onClick, expertRole, consult, variant } = useSubHealthWidget()
    return (
        <WidgetCard
            text={text}
            buttonText={buttonText}
            onClick={onClick}
            expertRole={expertRole}
            consult={consult}
            variant={variant}
        />
    )
}

const SubHealtDnaWidget = () => {
    const { text, buttonText, onClick, expertRole, consult } = useSubHealthDnaWidget()
    return (
        <WidgetCard
            text={text}
            buttonText={buttonText}
            onClick={onClick}
            expertRole={expertRole}
            consult={consult}
        />
    )
}

const SubNutritionWidget = () => {
    const { text, buttonText, onClick, expertRole, consult } = useSubNutritionWidget()
    return (
        <WidgetCard
            text={text}
            buttonText={buttonText}
            onClick={onClick}
            expertRole={expertRole}
            consult={consult}
        />
    )
}

const MetabolicHookWidget: React.FC<{ navigate: NavigateFunction }> = ({ navigate }) => {
    const { shouldPushBooking, hasFutureSpecialistAppointment, specConsultsLeft } = useSpecialistConsult();

    return shouldPushBooking ? (
        <WidgetCard text="👩‍⚕️ Prenota consulto con medico" buttonText="Prenota ora" onClick={() => navigate("/equipe/your-experts")} />
    ) : hasFutureSpecialistAppointment && specConsultsLeft ? (
        <ConsultCard consult={specConsultsLeft[0]} />
    ) : (
        <WidgetCard text="Hai completato tutti gli step" buttonText="Upsell" onClick={() => window.open("https://www.holifya.com/products/dna-complete", "_blank")} />
    );
};

const MetabolicSubWidget: React.FC<{ navigate: NavigateFunction }> = ({ navigate }) => {
    const { shouldPushBooking, hasFutureSpecialistAppointment, specConsultsLeft } = useSpecialistConsult();
    const { shouldPushNutritionistBooking, hasFutureNutritionistAppointment, nutriConsultsLeft } = useNutritionistConsult();
    const { isHealthAssessmentCompleted } = useHealthAssessment();
    const healthAssessmentCompleted = isHealthAssessmentCompleted()
    const { data: documents } = useGetDocuments();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    const filesUploaded =
        Array.isArray(documents) &&
        documents.some((doc) => new Date(doc.createdAt) > sixMonthsAgo);
    const metabolicUserType = useMetabolicUserType()

    const steps = [
        { condition: !healthAssessmentCompleted, text: "📝  Rispondi all'Health Assessment", buttonText: "Rispondi ora", onClick: () => navigate("/health-assessment-home-page") },
        { condition: !filesUploaded, text: "📄  Carica i tuoi referti", buttonText: "Carica ora", onClick: () => navigate("/reports") },
        { condition: shouldPushBooking, text: "👩‍⚕️  Prenota consulto con medico", buttonText: "Prenota ora", onClick: () => navigate("/equipe/your-experts") },
        { condition: hasFutureSpecialistAppointment && specConsultsLeft, component: <ConsultCard consult={specConsultsLeft[0]} /> },
        { condition: shouldPushNutritionistBooking && metabolicUserType === MetabolicUserType.Full, text: "👨‍⚕️️  Prenota consulto con nutrizionista", buttonText: "Prenota ora", onClick: () => navigate("/equipe/your-experts") },
        { condition: hasFutureNutritionistAppointment && nutriConsultsLeft, component: <ConsultCard consult={nutriConsultsLeft[0]} /> },
        { condition: true, text: "📝 Rispondi al check-up", buttonText: "Rispondi ora", onClick: () => navigate("/check-up-assessment-home-page") },
    ];
    const prioritizedStep = steps.find(({ condition }) => condition);
    const fallbackStep = {
        text: "📝 Rispondi al check-up",
        buttonText: "Rispondi ora",
        onClick: () => navigate("/check-up-assessment-home-page"),
    };

    return prioritizedStep
        ? (prioritizedStep.component ?? <WidgetCard text={prioritizedStep.text} buttonText={prioritizedStep.buttonText} onClick={prioritizedStep.onClick} />)
        : <WidgetCard {...fallbackStep} />;
};

// const WidgetCard: React.FC<{ text: string, buttonText: string, onClick: () => void }> = ({ text, buttonText, onClick }) => {
//     const { userData } = useUserStore()
//     const email = userData.email
//     const { sendEvent } = useCreateEvent()
//     const onButtonClick = async () => {
//         onClick()
//         if (!email) return
//         saveEvent(
//             email,
//             "control_panel_widget_button_clicked",
//             () => { },
//             () => { },
//             "control_panel_widget",
//         )
//         await sendEvent({
//             event: EventType.BUTTON_CLICK,
//             sub_category: "action_button",
//             event_metadata: { action: "control_panel_widget_main_action", button_text: buttonText }
//         })
//     }

//     return (
//         <div className="w-full px-[10px] py-[11px] rounded-[5px] border border-solid border-border shadow-platform-new flex items-center justify-between min-h-[50px]">
//             <span className="font-medium text-[14px]">{text}</span>
//             {
//                 buttonText && <PrimaryButton
//                     text={buttonText}
//                     pointer
//                     disabled={false}
//                     onClick={onButtonClick}
//                     className="!w-[86px] !h-[26px] !rounded-[3px] !text-[10px] !normal-case !text-[10px]"
//                 />
//             }
//         </div>
//     )
// };

const ConsultCard: React.FC<{ consult: Appointment }> = ({ consult }) => {
    const isUrgent = consult.remainingMinutes <= 1440;
    const { userData } = useUserStore()
    const email = userData.email
    const { sendEvent } = useCreateEvent()

    const onClick = async () => {
        window.open(consult.connectUrl, "_blank")
        if (!email) return
        saveEvent(
            email,
            "control_panel_widget_button_clicked",
            () => { },
            () => { },
            "control_panel_widget",
        )

        await sendEvent({
            event: EventType.BUTTON_CLICK,
            sub_category: "action_button",
            event_metadata: { action: "connect_to_appointment_from_control_panel_widget" }
        })
    }

    return (
        <div className={`w-full px-[10px] py-[11px] rounded-[5px] border border-solid ${isUrgent ? "border-holifya-blue" : "border-[#E5E5E5]"} shadow-platform-new flex items-center justify-between ${isUrgent ? "bg-[#D7E7FF]" : "bg-white"}`}>
            <div className="font-medium text-[14px]">
                <div>{consult.expert?.data?.name} {consult.expert?.data?.family_name}</div>
                <div className="text-[10px] text-holifya-blue">
                    {consult.expert?.role === ExpertRole.Specialist ? "Medico" : consult.expert?.role === ExpertRole.Physician ? "Endocrinologo" : "Nutrizionista"}
                </div>
            </div>
            <div className="flex items-center gap-2">
                <div className="font-medium text-[10px]">
                    <strong>{getDisplayDate(consult)}</strong> <span>{handleHourAndMinutes(consult.date)}</span>
                </div>
                <PrimaryButton
                    text="Connettiti"
                    pointer
                    disabled={false}
                    onClick={onClick}
                    className="!rounded-[3px] !w-[86px] !h-[26px] !normal-case !text-[10px]"
                />
            </div>
        </div>
    );
};

